// src/interface/admin-interface.ts
import { KnownErrors } from "../known-errors.js";
import { Result } from "../utils/results.js";
import { StackServerInterface } from "./server-interface.js";
var StackAdminInterface = class extends StackServerInterface {
  constructor(options) {
    super(options);
    this.options = options;
  }
  async sendAdminRequest(path, options, session, requestType = "admin") {
    return await this.sendServerRequest(
      path,
      {
        ...options,
        headers: {
          "x-stack-super-secret-admin-key": "superSecretAdminKey" in this.options ? this.options.superSecretAdminKey : "",
          ...options.headers
        }
      },
      session,
      requestType
    );
  }
  async sendAdminRequestAndCatchKnownError(path, requestOptions, tokenStoreOrNull, errorsToCatch) {
    try {
      return Result.ok(await this.sendAdminRequest(path, requestOptions, tokenStoreOrNull));
    } catch (e) {
      for (const errorType of errorsToCatch) {
        if (errorType.isInstance(e)) {
          return Result.error(e);
        }
      }
      throw e;
    }
  }
  async getProject() {
    const response = await this.sendAdminRequest(
      "/internal/projects/current",
      {
        method: "GET"
      },
      null
    );
    return await response.json();
  }
  async updateProject(update) {
    const response = await this.sendAdminRequest(
      "/internal/projects/current",
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(update)
      },
      null
    );
    return await response.json();
  }
  async createInternalApiKey(options) {
    const response = await this.sendAdminRequest(
      "/internal/api-keys",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(options)
      },
      null
    );
    return await response.json();
  }
  async listInternalApiKeys() {
    const response = await this.sendAdminRequest("/internal/api-keys", {}, null);
    const result = await response.json();
    return result.items;
  }
  async revokeInternalApiKeyById(id) {
    await this.sendAdminRequest(
      `/internal/api-keys/${id}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          revoked: true
        })
      },
      null
    );
  }
  async getInternalApiKey(id, session) {
    const response = await this.sendAdminRequest(`/internal/api-keys/${id}`, {}, session);
    return await response.json();
  }
  async listInternalEmailTemplates() {
    const response = await this.sendAdminRequest(`/internal/email-templates`, {}, null);
    const result = await response.json();
    return result.templates;
  }
  async listInternalEmailDrafts() {
    const response = await this.sendAdminRequest(`/internal/email-drafts`, {}, null);
    const result = await response.json();
    return result.drafts;
  }
  async createEmailDraft(options) {
    const response = await this.sendAdminRequest(
      `/internal/email-drafts`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(options)
      },
      null
    );
    return await response.json();
  }
  async updateEmailDraft(id, data) {
    await this.sendAdminRequest(
      `/internal/email-drafts/${id}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(data)
      },
      null
    );
  }
  async deleteEmailDraft(id) {
    await this.sendAdminRequest(
      `/internal/email-drafts/${id}`,
      {
        method: "DELETE"
      },
      null
    );
  }
  async listEmailThemes() {
    const response = await this.sendAdminRequest(`/internal/email-themes`, {}, null);
    const result = await response.json();
    return result.themes;
  }
  // Team permission definitions methods
  async listTeamPermissionDefinitions() {
    const response = await this.sendAdminRequest(`/team-permission-definitions`, {}, null);
    const result = await response.json();
    return result.items;
  }
  async createTeamPermissionDefinition(data) {
    const response = await this.sendAdminRequest(
      "/team-permission-definitions",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(data)
      },
      null
    );
    return await response.json();
  }
  async updateTeamPermissionDefinition(permissionId, data) {
    const response = await this.sendAdminRequest(
      `/team-permission-definitions/${permissionId}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(data)
      },
      null
    );
    return await response.json();
  }
  async deleteTeamPermissionDefinition(permissionId) {
    await this.sendAdminRequest(
      `/team-permission-definitions/${permissionId}`,
      { method: "DELETE" },
      null
    );
  }
  async listProjectPermissionDefinitions() {
    const response = await this.sendAdminRequest(`/project-permission-definitions`, {}, null);
    const result = await response.json();
    return result.items;
  }
  async createProjectPermissionDefinition(data) {
    const response = await this.sendAdminRequest(
      "/project-permission-definitions",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(data)
      },
      null
    );
    return await response.json();
  }
  async updateProjectPermissionDefinition(permissionId, data) {
    const response = await this.sendAdminRequest(
      `/project-permission-definitions/${permissionId}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(data)
      },
      null
    );
    return await response.json();
  }
  async deleteProjectPermissionDefinition(permissionId) {
    await this.sendAdminRequest(
      `/project-permission-definitions/${permissionId}`,
      { method: "DELETE" },
      null
    );
  }
  async getSvixToken() {
    const response = await this.sendAdminRequest(
      "/webhooks/svix-token",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({})
      },
      null
    );
    return await response.json();
  }
  async deleteProject() {
    await this.sendAdminRequest(
      "/internal/projects/current",
      {
        method: "DELETE"
      },
      null
    );
  }
  async getMetrics(includeAnonymous = false) {
    const params = new URLSearchParams();
    if (includeAnonymous) {
      params.append("include_anonymous", "true");
    }
    const queryString = params.toString();
    const response = await this.sendAdminRequest(
      `/internal/metrics${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET"
      },
      null
    );
    return await response.json();
  }
  async sendTestEmail(data) {
    const response = await this.sendAdminRequest(`/internal/send-test-email`, {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify(data)
    }, null);
    return await response.json();
  }
  async sendTestWebhook(data) {
    const response = await this.sendAdminRequest(`/internal/send-test-webhook`, {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify(data)
    }, null);
    return await response.json();
  }
  async listSentEmails() {
    const response = await this.sendAdminRequest("/internal/emails", {
      method: "GET"
    }, null);
    return await response.json();
  }
  async sendSignInInvitationEmail(email, callbackUrl) {
    await this.sendAdminRequest(
      "/internal/send-sign-in-invitation",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          callback_url: callbackUrl
        })
      },
      null
    );
  }
  async sendChatMessage(threadId, contextType, messages, abortSignal) {
    const response = await this.sendAdminRequest(
      `/internal/ai-chat/${threadId}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ context_type: contextType, messages }),
        signal: abortSignal
      },
      null
    );
    return await response.json();
  }
  async saveChatMessage(threadId, message) {
    await this.sendAdminRequest(
      `/internal/ai-chat/${threadId}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ message })
      },
      null
    );
  }
  async listChatMessages(threadId) {
    const response = await this.sendAdminRequest(
      `/internal/ai-chat/${threadId}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async applyWysiwygEdit(options) {
    const response = await this.sendAdminRequest(
      `/internal/wysiwyg-edit`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          source_type: options.sourceType,
          source_code: options.sourceCode,
          old_text: options.oldText,
          new_text: options.newText,
          metadata: options.metadata,
          dom_path: options.domPath.map((item) => ({ tag_name: item.tagName, index: item.index })),
          html_context: options.htmlContext
        })
      },
      null
    );
    const result = await response.json();
    return { updatedSource: result.updated_source };
  }
  async renderEmailPreview(options) {
    const response = await this.sendAdminRequest(`/emails/render-email`, {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({
        theme_id: options.themeId,
        theme_tsx_source: options.themeTsxSource,
        template_id: options.templateId,
        template_tsx_source: options.templateTsxSource,
        editable_markers: options.editableMarkers,
        editable_source: options.editableSource
      })
    }, null);
    return await response.json();
  }
  async createEmailTheme(displayName) {
    const response = await this.sendAdminRequest(
      `/internal/email-themes`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          display_name: displayName
        })
      },
      null
    );
    return await response.json();
  }
  async getEmailTheme(id) {
    const response = await this.sendAdminRequest(
      `/internal/email-themes/${id}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async updateEmailTheme(id, tsxSource) {
    await this.sendAdminRequest(
      `/internal/email-themes/${id}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          tsx_source: tsxSource
        })
      },
      null
    );
  }
  async deleteEmailTheme(id) {
    await this.sendAdminRequest(
      `/internal/email-themes/${id}`,
      {
        method: "DELETE"
      },
      null
    );
  }
  async updateEmailTemplate(id, tsxSource, themeId) {
    const response = await this.sendAdminRequest(
      `/internal/email-templates/${id}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ tsx_source: tsxSource, theme_id: themeId })
      },
      null
    );
    return await response.json();
  }
  async getConfig() {
    const response = await this.sendAdminRequest(
      `/internal/config`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async getConfigOverride(level) {
    const response = await this.sendAdminRequest(
      `/internal/config/override/${level}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async setConfigOverride(level, configOverride, source) {
    await this.sendAdminRequest(
      `/internal/config/override/${level}`,
      {
        method: "PUT",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          config_string: JSON.stringify(configOverride),
          ...source && { source }
        })
      },
      null
    );
  }
  async updateConfigOverride(level, configOverrideOverride) {
    await this.sendAdminRequest(
      `/internal/config/override/${level}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ config_override_string: JSON.stringify(configOverrideOverride) })
      },
      null
    );
  }
  async getPushedConfigSource() {
    const response = await this.sendAdminRequest(
      `/internal/config/source`,
      { method: "GET" },
      null
    );
    const data = await response.json();
    return data.source;
  }
  async unlinkPushedConfigSource() {
    await this.sendAdminRequest(
      `/internal/config/source`,
      { method: "DELETE" },
      null
    );
  }
  async resetConfigOverrideKeys(level, keys) {
    await this.sendAdminRequest(
      `/internal/config/override/${level}/reset-keys`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ keys })
      },
      null
    );
  }
  async createEmailTemplate(displayName) {
    const response = await this.sendAdminRequest(
      `/internal/email-templates`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          display_name: displayName
        })
      },
      null
    );
    return await response.json();
  }
  async deleteEmailTemplate(id) {
    await this.sendAdminRequest(
      `/internal/email-templates/${id}`,
      {
        method: "DELETE"
      },
      null
    );
  }
  async setupPayments() {
    const response = await this.sendAdminRequest(
      "/internal/payments/setup",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({})
      },
      null
    );
    return await response.json();
  }
  async getStripeAccountInfo() {
    const response = await this.sendAdminRequestAndCatchKnownError(
      "/internal/payments/stripe/account-info",
      {},
      null,
      [KnownErrors.StripeAccountInfoNotFound]
    );
    if (response.status === "error") {
      return null;
    }
    return await response.data.json();
  }
  async getPaymentMethodConfigs() {
    const response = await this.sendAdminRequestAndCatchKnownError(
      "/internal/payments/method-configs",
      { method: "GET" },
      null,
      [KnownErrors.StripeAccountInfoNotFound]
    );
    if (response.status === "error") {
      return null;
    }
    const data = await response.data.json();
    return {
      configId: data.config_id,
      methods: data.methods
    };
  }
  async updatePaymentMethodConfigs(configId, updates) {
    await this.sendAdminRequest(
      "/internal/payments/method-configs",
      {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ config_id: configId, updates })
      },
      null
    );
  }
  async createStripeWidgetAccountSession() {
    const response = await this.sendAdminRequest(
      "/internal/payments/stripe-widgets/account-session",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({})
      },
      null
    );
    return await response.json();
  }
  async listTransactions(params) {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (typeof params?.limit === "number") qs.set("limit", String(params.limit));
    if (params?.type) qs.set("type", params.type);
    if (params?.customerType) qs.set("customer_type", params.customerType);
    const response = await this.sendAdminRequest(
      `/internal/payments/transactions${qs.size ? `?${qs.toString()}` : ""}`,
      { method: "GET" },
      null
    );
    const json = await response.json();
    return { transactions: json.transactions, nextCursor: json.next_cursor };
  }
  async listSessionReplays(params) {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (typeof params?.limit === "number") qs.set("limit", String(params.limit));
    if (params?.user_ids && params.user_ids.length > 0) qs.set("user_ids", params.user_ids.join(","));
    if (params?.team_ids && params.team_ids.length > 0) qs.set("team_ids", params.team_ids.join(","));
    if (typeof params?.duration_ms_min === "number") qs.set("duration_ms_min", String(params.duration_ms_min));
    if (typeof params?.duration_ms_max === "number") qs.set("duration_ms_max", String(params.duration_ms_max));
    if (typeof params?.last_event_at_from_millis === "number") qs.set("last_event_at_from_millis", String(params.last_event_at_from_millis));
    if (typeof params?.last_event_at_to_millis === "number") qs.set("last_event_at_to_millis", String(params.last_event_at_to_millis));
    if (typeof params?.click_count_min === "number") qs.set("click_count_min", String(params.click_count_min));
    const response = await this.sendAdminRequest(
      `/internal/session-replays${qs.size ? `?${qs.toString()}` : ""}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async listSessionReplayChunks(sessionReplayId, params) {
    const qs = new URLSearchParams();
    if (params?.cursor) qs.set("cursor", params.cursor);
    if (typeof params?.limit === "number") qs.set("limit", String(params.limit));
    const response = await this.sendAdminRequest(
      `/internal/session-replays/${encodeURIComponent(sessionReplayId)}/chunks${qs.size ? `?${qs.toString()}` : ""}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async getSessionReplayChunkEvents(sessionReplayId, chunkId) {
    const response = await this.sendAdminRequest(
      `/internal/session-replays/${encodeURIComponent(sessionReplayId)}/chunks/${encodeURIComponent(chunkId)}/events`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async getSessionReplayEvents(sessionReplayId, options) {
    const qs = new URLSearchParams();
    if (typeof options?.offset === "number") qs.set("offset", String(options.offset));
    if (typeof options?.limit === "number") qs.set("limit", String(options.limit));
    const response = await this.sendAdminRequest(
      `/internal/session-replays/${encodeURIComponent(sessionReplayId)}/events${qs.size ? `?${qs.toString()}` : ""}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async refundTransaction(options) {
    const response = await this.sendAdminRequest(
      "/internal/payments/transactions/refund",
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({
          type: options.type,
          id: options.id,
          refund_entries: options.refundEntries.map((entry) => ({
            entry_index: entry.entryIndex,
            quantity: entry.quantity,
            amount_usd: entry.amountUsd
          }))
        })
      },
      null
    );
    return await response.json();
  }
  async previewAffectedUsersByOnboardingChange(onboarding, limit) {
    const response = await this.sendAdminRequest(
      `/internal/onboarding/preview-affected-users${limit ? `?limit=${limit}` : ""}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify({ onboarding })
      },
      null
    );
    return await response.json();
  }
  async queryAnalytics(options) {
    const response = await this.sendAdminRequest(
      "/internal/analytics/query",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          query: options.query,
          params: options.params ?? {},
          timeout_ms: options.timeout_ms ?? 1e3,
          include_all_branches: options.include_all_branches ?? false
        })
      },
      null
    );
    return await response.json();
  }
  async listOutboxEmails(options) {
    const qs = new URLSearchParams();
    if (options?.status) qs.set("status", options.status);
    if (options?.simple_status) qs.set("simple_status", options.simple_status);
    if (options?.limit !== void 0) qs.set("limit", options.limit.toString());
    if (options?.cursor) qs.set("cursor", options.cursor);
    const response = await this.sendServerRequest(
      `/emails/outbox${qs.size ? `?${qs.toString()}` : ""}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async getOutboxEmail(id) {
    const response = await this.sendServerRequest(
      `/emails/outbox/${id}`,
      { method: "GET" },
      null
    );
    return await response.json();
  }
  async updateOutboxEmail(id, data) {
    const response = await this.sendServerRequest(
      `/emails/outbox/${id}`,
      {
        method: "PATCH",
        headers: {
          "content-type": "application/json"
        },
        body: JSON.stringify(data)
      },
      null
    );
    return await response.json();
  }
};
export {
  StackAdminInterface
};
//# sourceMappingURL=admin-interface.js.map